import React, { useState, useEffect } from 'react';
import { X, Users } from 'lucide-react';
import api from '../../services/api';

const UserMultiSelect = ({
  selectedUserIds = [],
  onChange,
  disabled = false,
  label = "Assigned Users",
  placeholder = "Select users...",
  compact = false,
}) => {
  const [users, setUsers] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      console.log('🔍 UserMultiSelect: Fetching users...');
      const response = await api.get('/users/');
      console.log('✅ UserMultiSelect: API Response:', response);
      // The api interceptor already unwraps response.data, so response IS the data
      if (response.success) {
        console.log('✅ UserMultiSelect: Setting users:', response.data);
        setUsers(response.data);
      }
    } catch (error) {
      console.error('❌ UserMultiSelect: Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleUser = (userId) => {
    if (disabled) return;
    
    const newSelectedIds = selectedUserIds.includes(userId)
      ? selectedUserIds.filter(id => id !== userId)
      : [...selectedUserIds, userId];
    
    onChange(newSelectedIds);
  };

  const removeUser = (userId, e) => {
    e.stopPropagation();
    if (disabled) return;
    onChange(selectedUserIds.filter(id => id !== userId));
  };

  const getSelectedUsers = () => {
    return users.filter(user => selectedUserIds.includes(user._id || user.id));
  };

  const selectedUsers = getSelectedUsers();

  return (
    <div className={compact ? "space-y-1" : "space-y-2"}>
      <label className={compact ? "block text-xs font-medium text-gray-600" : "block text-sm font-medium text-gray-700"}>
        {label}
      </label>

      {/* Selected Users Display */}
      {selectedUsers.length > 0 && (
        <div className={`flex flex-wrap ${compact ? "gap-1 mb-1" : "gap-2 mb-2"}`}>
          {selectedUsers.map((user) => (
            <span
              key={user._id || user.id}
              className={`inline-flex items-center gap-1 bg-blue-100 text-blue-800 rounded-full ${compact ? "px-2 py-0.5 text-xs" : "px-3 py-1 text-sm"}`}
            >
              {user.name}
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => removeUser(user._id || user.id, e)}
                  className="hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {/* Dropdown */}
      <div className="relative">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={`w-full border border-gray-300 rounded-md text-left flex items-center justify-between ${
            compact ? "px-2 py-1.5 text-sm" : "px-3 py-2"
          } ${
            disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-gray-400'
          }`}
        >
          <span className={`flex items-center text-gray-600 ${compact ? "gap-1.5" : "gap-2"}`}>
            <Users className={compact ? "w-3.5 h-3.5" : "w-4 h-4"} />
            {selectedUsers.length > 0
              ? (compact ? `${selectedUsers.length} selected` : `${selectedUsers.length} user(s) selected`)
              : placeholder
            }
          </span>
          <svg
            className={`${compact ? "w-4 h-4" : "w-5 h-5"} transition-transform ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isOpen && !disabled && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-10"
              onClick={() => setIsOpen(false)}
            />
            
            {/* Dropdown Menu */}
            <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
              {loading ? (
                <div className="px-3 py-2 text-sm text-gray-500">Loading users...</div>
              ) : users.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No users available</div>
              ) : (
                <div className="py-1">
                  {users.map((user) => {
                    const userId = user._id || user.id;
                    const isSelected = selectedUserIds.includes(userId);
                    
                    return (
                      <button
                        key={userId}
                        type="button"
                        onClick={() => toggleUser(userId)}
                        className={`w-full px-3 py-2 text-left hover:bg-gray-100 flex items-center justify-between ${
                          isSelected ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900">
                            {user.name}
                          </span>
                          <span className="text-xs text-gray-500">
                            {user.email}
                          </span>
                        </div>
                        {isSelected && (
                          <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {selectedUsers.length === 0 && !disabled && !compact && (
        <p className="text-xs text-gray-500">
          No users assigned. Only admins can see unassigned records.
        </p>
      )}
    </div>
  );
};

export default UserMultiSelect;
